<script setup>
import { computed, ref } from 'vue';

import { usePlatform } from '../composables/usePlatform';
import { HrForm } from '../primitives';
import HrBottomSheet from '../primitives/HrBottomSheet.vue';
import HrFormDialog from './HrFormDialog.vue';

/**
 * Adaptive form dialog — renders as HrFormDialog on desktop and an HrBottomSheet form on mobile.
 *
 * @status recommended
 * @group cards-dialogs
 * @useWhen A form needs to open as a dialog on desktop and a bottom sheet on mobile
 * @avoidWhen A desktop-only or always-dialog form is needed — use HrFormDialog directly
 * @related HrFormDialog
 * @interfaceShape imperative
 */
defineOptions({ name: 'HrAdaptiveFormDialog' });

defineProps({
  /**
   * Dialog/sheet title shown in the header.
   * @example Edit profile
   */
  title: {
    type: String,
    required: true,
  },
  /**
   * Dialog width preset (desktop only).
   * @values sm, md, lg
   */
  size: {
    type: String,
    default: 'sm',
    validator: (v) => ['sm', 'md', 'lg'].includes(v),
  },
  /**
   * Prevents returning focus to the opener when the dialog closes (desktop). Set when opened from
   * a keyboard-activatable control (e.g. a clickable list row) to avoid a reopen-on-Enter loop.
   * No-op on mobile, where the bottom sheet has no opener to refocus.
   */
  noRefocus: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  /** Forwarded from the inner form on submit. */
  submit: null,
});

const isMobile = computed(() => usePlatform().is.mobile);

const formDialogRef = ref(null);
const sheetFormRef = ref(null);
const isSheetOpen = ref(false);

defineExpose({
  /** Opens the dialog (desktop) or bottom sheet (mobile). */
  // eslint-disable-next-line no-return-assign
  show: () => (isMobile.value ? (isSheetOpen.value = true) : formDialogRef.value?.show()),
  /** Closes the dialog (desktop) or bottom sheet (mobile). */
  // eslint-disable-next-line no-return-assign
  hide: () => (isMobile.value ? (isSheetOpen.value = false) : formDialogRef.value?.hide()),
  /** Programmatically submits the active form. */
  submit: () => (isMobile.value ? sheetFormRef.value?.submit() : formDialogRef.value?.submit()),
  /** Validates the active form; returns a promise resolving to true if valid. */
  validate: (shouldFocus) => (isMobile.value
    ? sheetFormRef.value?.validate(shouldFocus)
    : formDialogRef.value?.validate(shouldFocus)),
});
</script>

<template>
  <hr-form-dialog
    v-if="!isMobile"
    ref="formDialogRef"
    v-bind="$attrs"
    :title="title"
    :size="size"
    :no-refocus="noRefocus"
    @submit="emit('submit', $event)"
  >
    <!-- @slot Form fields and content; receives `{ isMobile }`. -->
    <slot :is-mobile="false" />
    <template
      v-if="$slots.actions"
      #actions
    >
      <!-- @slot actions Action buttons; receives `{ isMobile }`. -->
      <slot
        name="actions"
        :is-mobile="false"
      />
    </template>
  </hr-form-dialog>
  <hr-bottom-sheet
    v-else
    v-bind="$attrs"
    :model-value="isSheetOpen"
    :title="title"
    @update:model-value="isSheetOpen = $event"
  >
    <hr-form
      ref="sheetFormRef"
      @submit="emit('submit', $event)"
    >
      <slot :is-mobile="true" />
      <div
        v-if="$slots.actions"
        class="hr-adaptive-form-dialog__sheet-actions"
      >
        <slot
          name="actions"
          :is-mobile="true"
        />
      </div>
    </hr-form>
  </hr-bottom-sheet>
</template>

<style lang="scss">
.hr-adaptive-form-dialog__sheet-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 16px;
}
</style>
